import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchExpenditureByIdRequest,
  FetchExpendituresRequest,
  CreateExpenditureRequest,
  UpdateExpenditureRequest,
  DeleteExpenditureRequest,
} from './types'
import {AxiosError} from 'axios'
import axios from 'axios'
import {
  fetchExpensesUrl,
  fetchExpenseByIdUrl,
  createExpenseUrl,
  updateExpenseUrl,
  deleteExpenseUrl,
} from '../../api'

export const fetchExpenditures = createAsyncThunk(
  'expenditure/fetch',
  async (payload: FetchExpendituresRequest, {rejectWithValue}) => {
    try {
      // Build URL with all filters
      let url = `${fetchExpensesUrl}?sortField=${payload.sortField}&sortOrder=${payload.sortOrder}&pageNumber=${payload.pageNumber}&pageCount=${payload.pageCount}`
      
      if (payload.search && payload.search.trim() !== '') {
        url += `&search=${encodeURIComponent(payload.search.trim())}`
      }
      if (payload.status) {
        url += `&status=${encodeURIComponent(payload.status)}`
      }
      if (payload.paymentMode) {
        url += `&paymentMode=${encodeURIComponent(payload.paymentMode)}`
      }
      if (payload.startDate) {
        url += `&startDate=${encodeURIComponent(payload.startDate)}`
      }
      if (payload.endDate) {
        url += `&endDate=${encodeURIComponent(payload.endDate)}`
      }
      if (payload.establishement) {
        url += `&establishement=${encodeURIComponent(payload.establishement)}`
      }

      const res = await axios.get(url)
      return res.data
    } catch (error) {
      const axiosError = error as AxiosError
      return rejectWithValue({error: axiosError.response?.data})
    }
  }
)

export const fetchExpenditureById = createAsyncThunk(
  'expenditure/fetchById',
  async (payload: FetchExpenditureByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(fetchExpenseByIdUrl(payload.id))
      return res.data
    } catch (error) {
      const axiosError = error as AxiosError
      return rejectWithValue({error: axiosError.response?.data})
    }
  }
)

export const createExpenditure = createAsyncThunk(
  'expenditure/create',
  async (payload: CreateExpenditureRequest, {rejectWithValue}) => {
    try {
      const data = new FormData()
      
      // Required fields
      data.append('paymentTotal', payload.paymentTotal.toString())
      data.append('establishement', payload.establishement)
      data.append('user', payload.user) // REQUIRED by backend
      data.append('status', payload.status.toLowerCase()) // IMPORTANT: lowercase!
      data.append('paymentMode', payload.paymentMode)
      
      // Optional fields
      if (payload.paymentDate) data.append('paymentDate', payload.paymentDate)
      if (payload.description) data.append('description', payload.description)
      if (payload.nameOfTheBeneficiary) data.append('nameOfTheBeneficiary', payload.nameOfTheBeneficiary)
      
      // File attachments - IMPORTANT: Use 'files' field name for Multer!
      if (payload.attachements && payload.attachements.length > 0) {
        console.log('📎 Appending files:', payload.attachements.length)
        payload.attachements.forEach((file: File) => {
          console.log('📎 Appending file:', file.name, file.size)
          data.append('files', file)  // ✅ CORRECT: Use 'files' for upload
        })
      } else {
        console.log('📎 No files to upload')
      }

      // Log FormData contents
      console.log('📤 FormData contents:')
      for (const pair of (data as any).entries()) {
        console.log('  ', pair[0], ':', pair[1])
      }

      const res = await axios.post(createExpenseUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      console.log('✅ Create response:', res.data)
      console.log('✅ Created expense attachements:', res.data.expense?.attachements)
      return res.data
    } catch (error: any) {
      console.error('❌ Create Expenditure Error:', error)
      console.error('❌ Error Response:', error.response?.data)
      return rejectWithValue({
        error: error.response?.data || JSON.stringify(error),
      })
    }
  }
)

export const updateExpenditure = createAsyncThunk(
  'expenditure/update',
  async (payload: UpdateExpenditureRequest, {rejectWithValue}) => {
    try {
      console.log('✏️ Updating expenditure with ID:', payload.id)
      const data = new FormData()
      
      // REQUIRED: id field for POST /api/expenses/update
      data.append('id', payload.id)
      
      // Optional fields
      if (payload.paymentTotal !== undefined) data.append('paymentTotal', payload.paymentTotal.toString())
      if (payload.establishement) data.append('establishement', payload.establishement)
      if (payload.user) data.append('user', payload.user)
      if (payload.status) data.append('status', payload.status.toLowerCase()) // IMPORTANT: lowercase!
      if (payload.paymentMode) data.append('paymentMode', payload.paymentMode)
      if (payload.paymentDate) data.append('paymentDate', payload.paymentDate)
      if (payload.description) data.append('description', payload.description)
      if (payload.nameOfTheBeneficiary) data.append('nameOfTheBeneficiary', payload.nameOfTheBeneficiary)
      
      // New file attachments - IMPORTANT: Use 'files' field name for Multer!
      if (payload.attachements && payload.attachements.length > 0) {
        console.log('📎 UPDATE: Appending files:', payload.attachements.length)
        payload.attachements.forEach((file: File) => {
          console.log('📎 UPDATE: Appending file:', file.name, file.size)
          data.append('files', file)  // ✅ CORRECT: Use 'files' for upload
        })
      } else {
        console.log('📎 UPDATE: No new files to upload')
      }
      
      // Deleted attachments
      if (payload.deletedAttachements && payload.deletedAttachements.length > 0) {
        console.log('🗑️ UPDATE: Files to delete:', payload.deletedAttachements)
        data.append('deletedAttachements', JSON.stringify(payload.deletedAttachements))
      }

      // Log FormData contents
      console.log('📤 UPDATE: FormData contents:')
      for (const pair of (data as any).entries()) {
        if (pair[1] instanceof File) {
          console.log('  ', pair[0], ': [File]', pair[1].name)
        } else {
          console.log('  ', pair[0], ':', pair[1])
        }
      }

      // Use POST method (not PUT) to avoid CORS issues - matches backend spec
      const res = await axios.post(updateExpenseUrl, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      
      console.log('✅ Update response:', res.data)
      console.log('✅ Updated expense attachements:', res.data.expense?.attachements || res.data.attachements)

      return {
        expense: res.data.expense || res.data,
        success: true,
      }
    } catch (error: any) {
      console.error('❌ Update Expenditure Error:', error)
      console.error('❌ Error Response:', error.response?.data)
      console.error('❌ Error Status:', error.response?.status)
      return rejectWithValue({
        error: error.response?.data || error.message,
        success: false,
      })
    }
  }
)

export const deleteExpenditure = createAsyncThunk(
  'expenditure/delete',
  async (payload: DeleteExpenditureRequest, {rejectWithValue}) => {
    try {
      console.log('🗑️ Deleting expenditure with ID:', payload.id)
      console.log('🗑️ Delete URL:', deleteExpenseUrl)
      
      // Use POST with id in body (temporary workaround for CORS issue)
      const res = await axios.post(deleteExpenseUrl, {id: payload.id})
      console.log('✅ Delete response:', res.data)
      
      return {
        expense: res.data.expense || res.data.payment || res.data,
        success: true,
      }
    } catch (error: any) {
      console.error('❌ Delete Expenditure Error:', error)
      console.error('❌ Error Response:', error.response?.data)
      console.error('❌ Error Status:', error.response?.status)
      
      const axiosError = error as AxiosError
      return rejectWithValue({
        error: axiosError.response?.data || error.message,
        success: false,
      })
    }
  }
)
